<script setup>
import { ref, watch, computed } from 'vue'
import { Head, Link } from '@inertiajs/vue3';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import moment from 'moment';
import Paginator from 'primevue/paginator';

const props = defineProps({ userNotifications: Array })
const tabs = ref([
    { name: 'Ongelezen', href: '#', icon: 'fas', current: true },
    { name: 'Gelezen', href: '#', icon: 'far', current: false }
])
const selectTab = (selectedTab) => {
    tabs.value.forEach(tab => {
        tab.current = (tab === selectedTab)
    })
}

const userNotifications = props.userNotifications;
const readUserNotifications = userNotifications.filter(notification => notification.read == 1);
const unreadUserNotifications = userNotifications.filter(notification => notification.read != 1);

const pageNotifications = computed(() => props.userNotifications.filter(notification => notification.read === 1));

const notifications = ref(props.userNotifications.filter(notification => notification.read === 1));
const notificationsPaginator = ref(null);
const paginatedNotificationsData = ref([]);

watch(pageNotifications, () => {
  notifications.value = pageNotifications.value;
    //set paginator to page 1
    if (notificationsPaginator.value) {
        notificationsPaginator.value.changePage(0);
    }
})

watch(notifications, () => {
    if (notificationsPaginator.value) {
        notificationsPaginator.value.changePage(0);
    }
    changeNotificationsPagination({ rows: 10, page: 0 });
});

function changeNotificationsPagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedNotificationsData.value = notifications.value.slice(startIndex, endIndex);
}

changeNotificationsPagination({ rows: 10, page: 0 });
</script>

<template>
    <Head title="Notificaties | Mijnautosnelweg" />
    <TabGroup>
        <div>
            <div class="sm:hidden">
                <label for="tabs" class="sr-only">Select a tab</label>
                <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                <select id="tabs" name="tabs"
                    class="block w-full rounded-md border-gray-300 focus:border-brightorange focus:ring-brightorange">
                    <option v-for="tab in tabs" :key="tab.name" :selected="tab.current">{{ tab.name }}</option>
                </select>
            </div>
            <div class="hidden sm:flex justify-between mx-auto w-full max-w-6xl px-4 sm:px-6 py-6">
                <TabList class="flex gap-x-4">
                    <Tab v-for="tab in tabs" :key="tab.name" :href="tab.href"
                        :class="[tab.current ? 'border-brightorange text-brightorange' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700', 'group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium gap-x-2']"
                        :aria-current="tab.current ? 'page' : undefined" @click.prevent="selectTab(tab)">
                        <font-awesome-icon :icon="[tab.icon, 'bell']" :class="[tab.current ? 'text-brightorange' : 'text-gray-400 group-hover:text-gray-500', 'h-5 w-5 shrink-0']" aria-hidden="true" />
                        <span>{{ tab.name }}</span>
                    </Tab>
                </TabList>
            </div>
        </div>
        <TabPanels>
            <TabPanel class="mx-auto w-full max-w-6xl lg:flex lg:gap-x-16 px-4 sm:px-6 py-6">
                <h1 class="sr-only">Ongelezen notificaties</h1>
                <div class="w-full divide-y border-y">
                    <div class="flex flex-row gap-x-8 p-6" v-for="notification in unreadUserNotifications" v-if="unreadUserNotifications.length > 0">
                        <div class="flex flex-col gap-2" v-if="notification.type == 'text'">
                            <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                            <p class="text-gray-900 text-lg">{{notification.title}}</p>
                            <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                        </div>
                        <div class="flex flex-col gap-2" v-if="notification.type == 'task'">
                            <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                            <p class="text-gray-900 text-lg">{{notification.title}}</p>
                            <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                        </div>
                        <div class="flex flex-col lg:flex-row lg:justify-between gap-x-8" v-if="notification.type == 'lot'">
                            <div class="flex flex-col gap-2">
                                <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                                <p class="text-gray-900 text-lg">{{notification.title}}</p>
                                <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                            </div>
                            <div class="ring-1 p-2 flex flex-row rounded-lg w-96">
                                <div class="flex flex-col justify-between px-2">
                                    <div class="flex flex-col gap-1">
                                        <p class="text-gray-700 text-md">#{{notification.data.lot_id }}.</p>
                                        <p class="text-gray-500 text-sm text-ellipsis overflow-hidden ... truncate">
                                            
                                            {{notification.lot.make}} {{notification.lot.model}} {{notification.lot.body_type}}</p>
                                    </div>
                                    <div class="flex w-auto">
                                        <Link rel="stylesheet" :href="'/portal/kavel/' + notification.data.lot_id"
                                            class="gap-1 text-royalblue-500 hover:text-royalblue-500/80">
                                        Kavel bekijken
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-row gap-x-8" v-if="notification.type == 'bid'">
                            <div class="flex flex-col gap-2">
                                <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                                <p class="text-gray-900 text-lg">{{notification.title}}</p>
                                <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                            </div>
                        </div>
                    </div>
                    <div v-else class="p-5">
                        <p class="text-gray-500 text-sm text-center">Geen ongelezen notificaties</p>
                    </div>
                    
                </div>
            </TabPanel>
            <TabPanel class="mx-auto w-full max-w-6xl lg:flex lg:gap-x-16 px-4 sm:px-6 py-6">
                <h1 class="sr-only">Ongelezen notificaties</h1>
                <div class="w-full divide-y border-y">
                    <div class="flex flex-row gap-x-8 p-6" v-for="notification in paginatedNotificationsData" v-if="readUserNotifications.length > 0">
                        <div class="flex flex-col gap-2" v-if="notification.type == 'text'">
                            <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                            <p class="text-gray-900 text-lg">{{notification.title}}</p>
                            <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                        </div>
                        <div class="flex flex-col gap-2" v-if="notification.type == 'task'">
                            <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                            <p class="text-gray-900 text-lg">{{notification.title}}</p>
                            <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                        </div>
                        <div class="flex flex-col lg:flex-row lg:justify-between gap-x-8" v-if="notification.type == 'lot'">
                            <div class="flex flex-col gap-2">
                                <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                                <p class="text-gray-900 text-lg">{{notification.title}}</p>
                                <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                            </div>
                            <div class="ring-1 p-2 flex flex-row rounded-lg w-96">
                                <div class="flex flex-col justify-between px-2">
                                    <div class="flex flex-col gap-1">
                                        <p class="text-gray-700 text-md">#{{notification.data.lot_id }}.</p>
                                        <p class="text-gray-500 text-sm text-ellipsis overflow-hidden ... truncate">
                                            {{notification.lot.make}} {{notification.lot.model}} {{notification.lot.body_type}}</p>
                                    </div>
                                    <div class="flex w-auto">
                                        <Link rel="stylesheet" :href="'/portal/kavel/' + notification.data.lot_id"
                                            class="gap-1 text-royalblue-500 hover:text-royalblue-500/80">
                                        Kavel bekijken
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-row gap-x-8" v-if="notification.type == 'bid'">
                            <div class="flex flex-col gap-2">
                                <p class="text-gray-500 text-sm">{{moment(notification.created_at).format('DD-MM-YYYY HH:mm')}}</p>
                                <p class="text-gray-900 text-lg">{{notification.title}}</p>
                                <p class="text-gray-500 text-sm">{{ notification.body }}</p>
                            </div>
                        </div>
                    </div>
                    <div v-else class="p-5">
                        <p class="text-gray-500 text-sm text-center">Geen gelezen notificaties</p>
                    </div>

                    <Paginator ref="notificationsPaginator" v-if="notifications.length > 10" @page="changeNotificationsPagination" :rows="10"
                        :totalRecords="notifications.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
                </div>
            </TabPanel>
        </TabPanels>
    </TabGroup>
</template>
